import React, { useMemo } from 'react'
import './VariantPicker.css'

// selected: Record<propId,valueId>
// skus: [{ key: Record<propId,valueId>, price?, countInStock, image?, unavailable? }]
// variantProps: [{ id, name, type, values: [{ id, name, image?, hot?, disabled? }], displayOrder? }]
export default function VariantPicker({ variantProps = [], skus = [], value = {}, onChange, className = '' }) {
  const propsOrdered = useMemo(() => {
    const arr = Array.isArray(variantProps) ? [...variantProps] : []
    arr.sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
    return arr
  }, [variantProps])

  const selection = value || {}

  const handlePick = (propId, valId) => {
    const next = { ...selection, [propId]: valId }
    // Determine selectedSku if all props chosen
    let selectedSku = null
    const allChosen = propsOrdered.every(p => !!next[p.id])
    if (allChosen) {
      selectedSku = (skus || []).find(s => {
        const key = s?.key || {}
        return propsOrdered.every(p => {
          const target = next[p.id]
          return key[p.id] === target || key[p.id] === (variantProps.find(vp => vp.id === p.id)?.values.find(v => v.id === target)?.name)
        })
      }) || null
    }
    onChange && onChange({ selected: next, selectedSku })
  }

  return (
    <div className={`vp-root ${className}`}>
      {propsOrdered.map((prop) => {
        const currentVal = selection[prop.id]
        const title = `${prop.name}${currentVal ? `: ${prop.values?.find(v => v.id === currentVal)?.name || currentVal}` : ''}`
        return (
          <div key={prop.id} className='vp-block'>
            <div className='vp-title' aria-live='polite'>{title}</div>
            <div className='vp-options'>
              {(prop.values || []).map((val) => {
                const isSelected = currentVal === val.id
                // Determine availability of this value given other selections
                const matchingSkus = (skus || []).filter(s => {
                  const key = s?.key || {}
                  // Allow fallback match on value name if id mismatch (legacy data)
                  if (key[prop.id] !== val.id && key[prop.id] !== val.name) return false
                  // other props must match current selections
                  for (const p2 of propsOrdered) {
                    if (p2.id === prop.id) continue
                    const selVal = selection[p2.id]
                    if (selVal && key[p2.id] !== selVal) return false
                  }
                  return true
                })
                const hasAny = matchingSkus.length > 0
                const hasAvailable = matchingSkus.some(s => !s.unavailable)
                const hasUnavailable = matchingSkus.some(s => s.unavailable)
                const unavailableOnly = hasAny && !hasAvailable && hasUnavailable
                // Treat disabled flag as unavailable but still selectable for preview
                const rawDisabled = !!val.disabled
                const classes = [
                  'vp-option',
                  isSelected ? 'vp-option--selected' : '',
                  val.hot ? 'vp-option--hot' : '',
                  (unavailableOnly || rawDisabled || (!hasAny)) ? 'vp-option--unavailable' : '',
                ].filter(Boolean).join(' ')
                const commonProps = {
                  key: val.id,
                  className: classes,
                  onClick: () => { handlePick(prop.id, val.id) },
                  role: 'button',
                  'aria-pressed': isSelected,
                  'aria-disabled': false,
                  title: val.name + ((unavailableOnly || rawDisabled || !hasAny) ? ' (Unavailable)' : ''),
                  'data-unavailable': (unavailableOnly || rawDisabled || !hasAny) ? 'true' : undefined,
                }
                if (prop.type === 'image' && val.image) {
                  return (
                    <button type='button' {...commonProps}>
                      <img src={val.image} alt={val.name} className='vp-thumb' />
                      {(unavailableOnly || rawDisabled || !hasAny) && <span className='vp-unavailable-badge'>Unavailable</span>}
                    </button>
                  )
                }
                return (
                  <button type='button' {...commonProps}>
                    {val.name}
                    {(unavailableOnly || rawDisabled || !hasAny) && <span className='vp-unavailable-badge'>Unavailable</span>}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
